import { Mediaclip } from './../interfaces/mediaclip';
import { SapiService } from './sapi.service';
import { LocationService } from './location.service';
import { Router } from '@angular/router';
import { AuthenticationService } from './authentication.service';
import { Injectable } from '@angular/core';
import { LocalStorageService } from 'angular-2-local-storage';
import { Observable, BehaviorSubject} from 'rxjs';

export interface Worklist {
	isOpen: boolean;
	activeItem?: Mediaclip;
	items: WorklistItem[];
}

export interface WorklistItem extends Mediaclip {
	id: string;
	mediatype: string;
	title: string;
	originalfilename: string;
	status: string;
	visible: string;
	listtype: string;
}

@Injectable()
export class WorklistService {

	worklist: Worklist;
	protected worklistSubject: BehaviorSubject<Worklist>;

	constructor(
		protected localStorage: LocalStorageService,
		protected authenticationService: AuthenticationService,
		protected router: Router,
		protected locationService: LocationService,
		protected sapiService: SapiService) {

		this.worklist = localStorage.get('ovp-worklist');
		if (!this.worklist || typeof this.worklist !== 'object') {
			this.worklist = { isOpen: false, items: [] };
		}
		if (!this.worklist && !this.worklist.items) {
			this.worklist.items = [];
		}

		this.worklistSubject = new BehaviorSubject<Worklist>(this.worklist);

		if (this.worklist.isOpen) {
			this.worklist.isOpen = false;
			this.toggle();
		}
	}

	public getTheWorklist(): Observable<Worklist> {
		return this.worklistSubject.asObservable();
	}

	shouldShow() {
		const evl = this.router.url.includes('/library/') && window.screen.width > 750;

		return evl;
	}

	updateStorage() {
		this.localStorage.set('ovp-worklist', this.worklist);
		this.worklistSubject.next(this.worklist);
	}

	createWorklistItemFromData(data: any): WorklistItem {
		return {
			id: data.id,
			mediatype: data.mediatype,
			title: data.title,
			originalfilename: data.originalfilename,
			status: data.status,
			visible: data.visible,
			listtype: data.listtype,
			isEmpty: data.isEmpty
		};
	}

	updateItemsById(items: WorklistItem[], response?: any) {
		items.forEach((item: WorklistItem) => {
			// Multiple items are updated in this case
			if (response) {
				response.messages.forEach((messageData: any) => {
					const matches: RegExpMatchArray = messageData.message.match('set ([^ ]+) to ([^ ]+) for clip (.*)');
					if (matches.length > 3) {
						const property = matches[1];
						const value = matches[2];
						const id = matches[3];
						const index = this.indexOfItemById(id);

						if (index > -1) {
							const newItem = JSON.parse(JSON.stringify(this.worklist.items[index]));
							newItem[property] = value;
							this.worklist.items[index] = newItem;
						}
					}

				});
			} else {
				const index = this.indexOfItemById(item.id);
				if (index > -1) {
					this.worklist.items[index] = this.createWorklistItemFromData(item);
				}
			}
		});
		this.updateStorage();
	}

	addItems(items: any[]) {
		let pushed = 0;
		// iterate over all items that need to be pushed
		for (let i = 0; i < items.length; i++) {

			let data = items[i].data;

			if (items.length === 1) {
				data = items[i];
			}

			// add if the item is not already in the list
			if (!this.inWorklistById(data.id)) {
				this.worklist.items.push(this.createWorklistItemFromData(data));
				pushed++;
			}
		}

		if (!this.worklist.isOpen) {
			this.toggle();
		}

		this.updateStorage();
		return pushed;
	}

	removeItemsById(itemsById: (string | number)[]) {
		itemsById.forEach( id => {
			if (this.inWorklistById(id)) {
				this.worklist.items.splice(this.indexOfItemById(id), 1);

				if (this.worklist.activeItem !== null && id === this.worklist.activeItem.id) {
					this.setActiveItem(null);
				}
			}
		});
		this.updateStorage();
	}

	clear() {
		this.worklist.items = new Array();
		this.toggle();
		this.updateStorage();
	}

	indexOfItemById(itemId: string | number) {
		for (let i = 0; i < this.worklist.items.length; i++) {
			if ( this.worklist.items[i].id === itemId) {
				return i;
			}
		}
		return -1;
	}

	inWorklistById(itemId: string | number) {
		let idCheck = false;

		// check if the id is in there
		for (let i = 0; i < this.worklist.items.length; i++) {
			if ( this.worklist.items[i].id === itemId) {
				idCheck = true;
			}
		}

		return idCheck;
	}

	setActiveItem(item: Mediaclip) {
		this.worklist.activeItem = item;
		this.updateStorage();
	}

	setActiveItemById(id: string) {
		if (this.worklist && this.inWorklistById(id) && (!this.worklist.activeItem || this.worklist.activeItem.id !== id)) {
			this.setActiveItem( this.worklist.items[this.indexOfItemById(id)] );
		}
	}

	toggle() {
		this.worklist.isOpen = !this.worklist.isOpen;
		this.updateStorage();
	}

	incrementItem(delta: number) {
		// find the index of the new item
		let newIndex = this.indexOfItemById(this.worklist.activeItem.id) + delta;

		// ensure you can't get out of the array bounds
		if (newIndex >= this.worklist.items.length) {
			newIndex = 0;
		} else if (newIndex < 0) {
			newIndex = (this.worklist.items.length - 1);
		}

		// set the active item
		this.setActiveItem(this.worklist.items[newIndex]);

		// navigate to the correct item :)
		this.router.navigate(['library/mediaclip/', this.worklist.activeItem.id, 'metadata']);
	}
}
